import {
  AfterViewInit,
  Component,
  OnInit,
} from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import {
  SharedModule,
  StringCustomFieldConfig,
  FormInputComponent,
  DataService,
  NotificationService,
  ProductVariant,
} from '@vendure/admin-ui/core';
import { safeJSONParse } from '../utils';
import { defaultSimpleProductInfoData, SimpleProductInfoData } from '../ui-types';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'simple-product-info-section-form',
  template: `
    <vdr-card>
      <!-- Product Variant -->
      <div class="form-row mb-2">
        <vdr-form-field label="Product Variant" class="form-item">
          <vdr-dynamic-form-input
            #productVariantInput
            [control]="productVariantCtrl"
            [def]="{
              type: 'relation',
              entity: 'ProductVariant',
              ui: { component: 'relation-form-input' }
            }"
          ></vdr-dynamic-form-input>
        </vdr-form-field>
      </div>

      <!-- Display Options -->
      <p><strong>Display Options</strong></p>
      <div class="mt-1 options-grid">
        <label class="option-item" *ngFor="let opt of booleanOptions; let i = index">
          <input type="checkbox" class="option-checkbox" [formControl]="opt.control" />
          <span class="option-label">{{ opt.label }}</span>
        </label>
      </div>
  `,
  styleUrls: [
    '../styles.scss',
  ],
  standalone: true,
  imports: [SharedModule],
})
export class SimpleProductInfoComponent
  implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit
{
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl!: FormControl;

  data: SimpleProductInfoData;
  private destroyed$ = new Subject<void>();
  productVariantCtrl = new UntypedFormControl();

  displayThumbnailsCtrl = new FormControl(false);
  displayFeaturedReviewCtrl = new FormControl(false);
  displaySkuCtrl = new FormControl(false);
  displayProductFeaturesCtrl = new FormControl(false);
  displayProductDescriptionCtrl = new FormControl(false);
  displayReviewInfoCtrl = new FormControl(false);
  displayProductSubtitleCtrl = new FormControl(false);
  displayPaymentCapabilitiesCtrl = new FormControl(false);
  displayProductOptionsCtrl = new FormControl(false);
  displayAddToCartCtrl = new FormControl(false);
  displayProductKeyPointsCtrl = new FormControl(false);
  displayProductPageEntryCtrl = new FormControl(false);

  booleanOptions = [
    { control: this.displayThumbnailsCtrl, label: 'Product Assets', key: 'displayThumbnails' },
    { control: this.displayFeaturedReviewCtrl, label: 'Featured Review', key: 'displayFeaturedReview' },
    { control: this.displayProductSubtitleCtrl, label: 'Product Subtitle', key: 'displayProductSubtitle' },
    { control: this.displaySkuCtrl, label: 'SKU', key: 'displaySku' },
    { control: this.displayReviewInfoCtrl, label: 'Review Info', key: 'displayReviewInfo' },
    { control: this.displayProductOptionsCtrl, label: 'Product Options', key: 'displayProductOptions' },
    { control: this.displayAddToCartCtrl, label: 'Add-to-Cart Button', key: 'displayAddToCart' },
    { control: this.displayPaymentCapabilitiesCtrl, label: 'Payment Options', key: 'displayPaymentCapabilities' },
    { control: this.displayProductDescriptionCtrl, label: 'Product Description', key: 'displayProductDescription' },
    { control: this.displayProductKeyPointsCtrl, label: 'Product Key Points', key: 'displayProductKeyPoints' },
    { control: this.displayProductFeaturesCtrl, label: 'Product Features', key: 'displayProductFeatures' },
    { control: this.displayProductPageEntryCtrl, label: 'Product Page Entry', key: 'displayProductPageEntry' },
  ];

  constructor(
    private notificationService: NotificationService,
    private dataService: DataService,
  ) {}

  ngOnInit(): void {
    this.data = safeJSONParse<SimpleProductInfoData>(
      this.formControl.value,
      this.notificationService,
      defaultSimpleProductInfoData,
    )!;
    this.initControlsFromData();
    if (this.data.productVariantId) {
      this.dataService.product
        .getProductVariant(this.data.productVariantId.toString())
        .mapSingle((d) => d.productVariant)
        .pipe(takeUntil(this.destroyed$)).subscribe((variant) => this.productVariantCtrl.setValue(variant));
    }
  }

  ngAfterViewInit(): void {
    this.productVariantCtrl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((v: ProductVariant | null) =>
      this.updateData('productVariantId', v ? v.id : null),
    );
    for (const { control, key } of this.booleanOptions) {
      control.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((val: boolean | null) =>
        this.updateData(key as keyof SimpleProductInfoData, !!val)
      );
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private initControlsFromData(): void {
    this.booleanOptions.forEach(({ control, key }) => {
      control.setValue(this.data[key as keyof SimpleProductInfoData] as boolean);
    });
  }

  private updateData<K extends keyof SimpleProductInfoData>(key: K, value: SimpleProductInfoData[K]): void {
    if (this.data[key] === value) return;

    this.data[key] = value;
    this.formControl.setValue(JSON.stringify(this.data));
    this.formControl.markAsDirty();
  }
}